import { useCallback } from 'react'
import {
  ArrayInput,
  EditController,
  EditView,
  NumberInput,
  required,
  SaveButton,
  SelectInput,
  SimpleForm,
  SimpleFormIterator,
  TextInput,
  Toolbar,
  useNotify,
  useRedirect,
  useUpdate,
} from 'react-admin'

import { useFeeItemTypes } from 'helpers/useFeeItemTypes'
import { STATEMENT_LINE_ITEM_TYPE_CHOICES, StatementType } from '../../helpers/constants'
import {
  validateStatementAmounts,
  validateStatementFeePercent,
  validateStatementItems,
  validateStatementTotalCap,
} from '../../helpers/validators'

const redirect = (data: any) => {
  return data.job ? `/jobs/${data.job}/show/statements` : `/statements/${data.id}/show`
}

const StatementEditSaveButton = (props: any) => {
  const [update] = useUpdate('statements')
  const redirectTo = useRedirect()
  const notify = useNotify()
  const { record } = props

  const handleSave = useCallback(
    (values, redirect) => {
      update(
        {
          payload: { id: values.id, data: { ...values }, previousData: record },
        },
        {
          onSuccess: ({ data: newRecord }) => {
            notify('Statement saved', 'info', { smart_count: 1 })
            redirectTo(redirect, newRecord)
          },
          onFailure: (error) => {
            notify(`${JSON.stringify(error)}`, { type: 'error' })
          },
        }
      )
    },
    [notify, redirectTo, update, record]
  )

  return <SaveButton {...props} onSave={handleSave} />
}

const StatemenEditToolbar = (props: any) => (
  <Toolbar {...props}>
    <StatementEditSaveButton />
  </Toolbar>
)

const StatementEdit = (props: any) => {
  const { feeItemTypes } = useFeeItemTypes()
  return (
    <EditController undoable={false} {...props}>
      {(controllerProps) => (
        <EditView {...props} {...controllerProps}>
          <SimpleForm toolbar={<StatemenEditToolbar />} redirect={redirect} validate={validateStatementItems}>
            <NumberInput source="lease_fee_percent" validate={validateStatementFeePercent} />
            <NumberInput source="service_fee_percent" validate={validateStatementFeePercent} />
            <TextInput
              source="description"
              validate={
                controllerProps.record && controllerProps.record.type === StatementType.JobCorrection
                  ? required()
                  : undefined
              }
            />
            <ArrayInput source="line_items">
              <SimpleFormIterator>
                <SelectInput
                  source="type"
                  choices={STATEMENT_LINE_ITEM_TYPE_CHOICES}
                  label="Line item type"
                  validate={required()}
                />
                <NumberInput
                  source="amount"
                  label="Amount"
                  validate={[validateStatementAmounts, validateStatementTotalCap]}
                />
                <TextInput source="description" label="Description" />
              </SimpleFormIterator>
            </ArrayInput>
            <ArrayInput source="fee_items">
              <SimpleFormIterator>
                <NumberInput
                  source="amount"
                  label="Amount"
                  validate={[validateStatementAmounts, validateStatementTotalCap]}
                />
                <TextInput source="description" label="Description" validate={required()} />
                <SelectInput label="Type" source="type" choices={feeItemTypes} alwaysOn />
              </SimpleFormIterator>
            </ArrayInput>
          </SimpleForm>
        </EditView>
      )}
    </EditController>
  )
}

export default StatementEdit
